'use client'

import { useState } from 'react'
import { ArrowUpRight } from '@/components/ui/icons'
import { CopyButton } from './CopyButton'

export type Door = { id: string; line: string; code: string; docs: string }

// One code panel with three doors in front of it. Every panel is in the markup (crawlers and
// the no-JS case read all three); the inactive ones are hidden, not removed.
export function ConnectTabs({ doors }: { doors: Door[] }) {
  const [active, setActive] = useState(doors[0].id)

  return (
    <div className="overflow-hidden rounded-2xl border border-white/[0.09] bg-[#0e0e0e]">
      <div role="tablist" aria-label="Ways to connect" className="flex border-b border-white/[0.08]">
        {doors.map(d => {
          const on = d.id === active
          return (
            <button
              key={d.id}
              type="button"
              role="tab"
              id={`connect-tab-${d.id}`}
              aria-selected={on}
              aria-controls={`connect-panel-${d.id}`}
              onClick={() => setActive(d.id)}
              className={`-mb-px border-b-2 px-5 py-3.5 font-mono text-[13px] font-semibold transition-colors sm:px-7 ${
                on ? 'border-white text-white' : 'border-transparent text-white/40 hover:text-white/75'
              }`}
            >
              {d.id}
            </button>
          )
        })}
      </div>
      {doors.map(d => (
        <div
          key={d.id}
          role="tabpanel"
          id={`connect-panel-${d.id}`}
          aria-labelledby={`connect-tab-${d.id}`}
          hidden={d.id !== active}
          className="p-5 sm:p-6"
        >
          <p className="text-[15px] leading-relaxed text-white/60">{d.line}</p>
          <pre className="mt-4 whitespace-pre-wrap break-all rounded-xl border border-white/[0.1] bg-black/40 px-4 py-4 font-mono text-[12.5px] leading-relaxed text-white/80">
            {d.code}
          </pre>
          <div className="mt-4 flex items-center justify-between gap-4">
            <CopyButton text={d.code} />
            <a
              href={d.docs}
              data-cta={`agents_docs_${d.id.toLowerCase()}`}
              className="group inline-flex items-center gap-1 text-[14px] text-white/70 transition-colors hover:text-white"
            >
              {d.id} docs
              <ArrowUpRight className="h-4 w-4 text-white/40 transition-colors group-hover:text-white" />
            </a>
          </div>
        </div>
      ))}
    </div>
  )
}
